import React, { useContext, useMemo, useRef } from 'react';
import {
  useEventCallback,
  SnackbarContext,
  DetailContext,
  UserContext,
  DetailHandle,
  abortableFetch,
  AbortableFetch,
  PromptContext,
  usePrompts,
  FormTextArea,
} from '@eas/common-web';
import { Permission, EvidenceAPI, Messages } from '../../../enums';
import {
  IrzFacilityRequest,
  IrzRequestState,
  IrzRequestType,
} from '../../../models';
import { unstable_batchedUpdates } from 'react-dom';

interface FormData {
  note?: string;
}

/**
 * Api call
 * @param id
 */
export function apiCall(id: string, data: FormData) {
  return abortableFetch(`${EvidenceAPI.IRZ_FACILITY_REQUESTS}/${id}/reject`, {
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    method: 'POST',
    body: JSON.stringify(data),
  });
}

const PROMPT_KEY = 'REJECT';

export function useRejectDialog() {
  /**
   * Context stuff.
   */
  const { testPrompt } = useContext(PromptContext);
  const { showSnackbar } = useContext(SnackbarContext);
  const { source, onPersisted } = useContext<DetailHandle<IrzFacilityRequest>>(
    DetailContext
  );
  const { hasPermission } = useContext(UserContext);

  /**
   * Fetch ref.
   */
  const fetch = useRef<AbortableFetch | null>(null);

  /**
   * Dialogs
   */
  usePrompts([
    {
      key: PROMPT_KEY,
      dialogTitle: 'Zamítnutí žádosti',
      dialogText: 'Skutečně chcete zamítnout žádost provozovny IRZ?',
      FormFields: function Body() {
        return (
          <>
            <FormTextArea name="note" label="Poznámka" />
          </>
        );
      },
    },
  ]);

  const handleReject = useEventCallback(() => {
    testPrompt({
      key: PROMPT_KEY,
      submitCallback: async (values: FormData) => {
        if (source.data) {
          try {
            source.setLoading(true);
            if (fetch.current !== null) {
              fetch.current.abort();
            }
            fetch.current = apiCall(source.data.id, values);

            await fetch.current.raw();

            unstable_batchedUpdates(() => {
              showSnackbar(...Messages.IrzFacilityRequest.REJECT.SUCCESS);
              source.setLoading(false);
            });

            await source.refresh();
            onPersisted(source.data.id);
          } catch (err) {
            source.setLoading(false);
            if (err.name !== 'AbortError') {
              showSnackbar(...Messages.IrzFacilityRequest.REJECT.ERROR);
              throw err;
            }
            return undefined;
          }
        }
      },
    });
  });

  const canFinish = useMemo(() => {
    switch (source.data?.type) {
      case IrzRequestType.REGISTRATION:
        return hasPermission(Permission.IrzFacility.IRZ_REQUEST_CREATE_FINISH);
      case IrzRequestType.CHANGE:
        return hasPermission(Permission.IrzFacility.IRZ_REQUEST_UPDATE_FINISH);
      case IrzRequestType.OPERATOR_CHANGE:
        return (
          hasPermission(
            Permission.IrzFacility.IRZ_REQUEST_CHANGE_OPERATOR_FINISH_ALL
          ) ||
          hasPermission(
            Permission.IrzFacility.IRZ_REQUEST_CHANGE_OPERATOR_FINISH_OWN,
            {
              subjectId: source.data.relatedSubject?.id,
            }
          )
        );
      case IrzRequestType.INVALIDATION:
        return hasPermission(
          Permission.IrzFacility.IRZ_REQUEST_INVALIDATE_FINISH
        );
    }
  }, [source.data?.type, source.data?.relatedSubject?.id]);

  const showRejectButton =
    canFinish &&
    source.data?.state === IrzRequestState.WAITING_FOR_AUTHORIZATION;

  return {
    showRejectButton,
    handleReject,
  };
}
